import { useCallback, useEffect } from 'react';
import { ScrollView } from 'react-native';
import {
  Colors,
  Flex,
  IconArrowRight,
  IconDownload,
  Typography,
} from '@banhala/ui-kit';
import type {
  DownloadableCouponsProps,
  DownloadableCouponEntity,
} from './types';
import { bridge, isNotNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

export const DownloadableCoupons = ({
  entity,
  body,
  logging,
}: DownloadableCouponsProps) => {
  const { openDeeplink } = useOpenDeeplink();

  const handlePressViewAll = useCallback(() => {
    if (!body?.item.more_deeplink) return;
    bridge.logAnalyticsEvent({
      name: 'CLICK_MORE',
      analytics: { ...logging?.analytics, POSITION: '전체보기' },
      ably_only_log: { ...logging?.ably_only_log },
    });
    openDeeplink(body.item.more_deeplink);
  }, [body, logging, openDeeplink]);

  if (!entity || entity.length === 0) return null;

  return (
    <Flex
      padding={{ top: 8, horizontal: 16, bottom: 16 }}
    >
      <Flex
        backgroundColor="bg_secondary"
        borderRadius={8}
        padding={{ bottom: 16 }}
      >
        {isNotNil(body) && (
          <Flex.Row
            alignItems="center"
            justifyContent="space-between"
            padding={{ horizontal: 16, vertical: 12 }}
          >
            <Typography type="subtitle1" color="content_primary">
              {body.item.title}
            </Typography>
            <Flex onPress={handlePressViewAll}>
              <Typography type="body3" color="content_secondary">
                전체보기
              </Typography>
            </Flex>
          </Flex.Row>
        )}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            paddingHorizontal: 16,
            gap: 4,
          }}
        >
          {entity.map((coupon, index) => (
            <CouponCard
              key={coupon.item.sno}
              coupon={coupon}
              logging={logging}
              index={index}
            />
          ))}
          {isNotNil(body) && (
            <MoreButton onPress={handlePressViewAll} />
          )}
        </ScrollView>
      </Flex>
    </Flex>
  );
};

const CouponCard = ({
  coupon,
  logging,
  index,
}: {
  coupon: DownloadableCouponEntity;
  logging: IScreensLogging;
  index: number;
}) => {
  const { item } = coupon;
  const { openDeeplink } = useOpenDeeplink();

  useEffect(() => {
    bridge.logAnalyticsEvent({
      name: 'DISPLAY_COUPON',
      analytics: { ...logging?.analytics, ...coupon.logging?.analytics, INDEX: index },
      ably_only_log: { ...logging?.ably_only_log, ...coupon.logging?.ably_only_log },
    });
  }, [logging, coupon.logging, index]);

  const handlePress = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_COUPON',
      analytics: { ...logging?.analytics, ...coupon.logging?.analytics, INDEX: index },
      ably_only_log: { ...logging?.ably_only_log, ...coupon.logging?.ably_only_log },
    });
    openDeeplink(item.deeplink);
  }, [item.deeplink, logging, coupon.logging, index, openDeeplink]);

  return (
    <Flex
      width={120}
      height={128}
      backgroundColor="white"
      borderRadius={8}
      onPress={handlePress}
      padding={{ horizontal: 12, vertical: 12 }}
      justifyContent="space-between"
    >
      <Flex gap={4}>
        <Typography
          type="h3"
          color={item.is_issuable ? 'content_pink_primary' : 'content_secondary'}
          numberOfLines={1}
        >
          {item.discount_text}
        </Typography>
        <Typography
          type="body4"
          color="content_secondary"
          numberOfLines={2}
        >
          {item.name}
        </Typography>
      </Flex>
      <Flex alignItems="flex-end">
        {item.is_issuable ? (
          <IconDownload size={20} fill={Colors.content_primary} />
        ) : (
          <IconArrowRight size={20} fill={Colors.content_secondary} />
        )}
      </Flex>
    </Flex>
  );
};

const MoreButton = ({ onPress }: { onPress: () => void }) => (
  <Flex
    width={120}
    height={128}
    alignItems="center"
    justifyContent="center"
    onPress={onPress}
  >
    <Flex
      width={40}
      height={40}
      borderRadius={20}
      borderWidth={1}
      borderColor="border_tertiary"
      alignItems="center"
      justifyContent="center"
    >
      <IconArrowRight size={20} fill={Colors.content_secondary} />
    </Flex>
  </Flex>
);
